import { Sparkle, CheckCircle, X as XIcon, Crown } from "@phosphor-icons/react";

/**
 * BidoriaBanner — pixel-locked share asset.
 *
 * Two variants via ?v= query param:
 *   ?v=square   1080 x 1080  (Instagram, X post)
 *   ?v=wide     1200 x 630   (Open Graph, LinkedIn, X card)
 *   ?v=table    1080 x 1280  (Just the comparison table, tall)
 *
 * Designed to be screenshotted by Playwright at the exact viewport size.
 * No nav, no footer, no scroll. Pure asset.
 */
export default function BidoriaBanner() {
  const variant =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("v") || "wide"
      : "wide";

  const cells = [
    ["Live auction streaming",                   true,  true],
    ["Bidoria on stream audio",                  true,  false],
    ["9 voice personalities, real speech",       true,  false],
    ["Custom seller catchphrases",               true,  false],
    ["Real-time bid-aware reactions",            true,  false],
    ["Strict wholesale/retail segregation",      true,  false],
    ["Founder Seller scarcity tier",             true,  false],
    ["$0 marginal cost to seller",               true,  false],
  ];

  if (variant === "wide") {
    // 1200 x 630 — Open Graph standard (X cards, LinkedIn, FB)
    return (
      <div
        className="bg-zinc-950 text-white"
        style={{ width: 1200, height: 630, position: "relative", overflow: "hidden" }}
        data-testid="bidoria-banner-wide"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 18% 35%, rgba(245,158,11,0.25), transparent 55%), radial-gradient(circle at 80% 70%, rgba(29,120,171,0.20), transparent 55%)",
          }}
        />
        <div className="relative h-full grid grid-cols-[1.05fr_1fr] gap-10 p-12">
          {/* LEFT: Brand pitch */}
          <div className="flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-300 font-bold mb-5">
                <Crown size={12} weight="fill" />
                Bidorah · Bidoria
              </div>
              <h1 className="text-[44px] font-black tracking-tight leading-[1.02]">
                The only live<br />
                auction app with{" "}
                <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                  Bidoria.
                </span>
              </h1>
              <p className="mt-5 text-[16px] text-white/70 leading-relaxed max-w-[360px]">
                A real Bidoria that hypes your bidders, drops market comps,
                and sounds uniquely yours.
              </p>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-black bg-gradient-to-br from-amber-200 via-amber-400 to-orange-500 bg-clip-text text-transparent">
                    Bidoria
                  </span>
                  <span className="text-white/40 text-[11px] uppercase tracking-[0.25em] font-bold">by</span>
                  <span className="text-2xl font-black text-white">Bidorah</span>
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white/35 font-bold">
                  bidorah.com/demo
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Mini comparison table */}
          <div className="rounded-2xl bg-black/60 border border-amber-400/30 overflow-hidden self-center w-full backdrop-blur-md shadow-2xl">
            <div className="bg-amber-500/10 border-b border-amber-400/20 px-4 py-2 text-center">
              <span className="text-[9px] uppercase tracking-widest text-amber-300 font-bold">
                Bidoria vs. the rest
              </span>
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-white/10 text-white/55 text-[8.5px] uppercase tracking-wider">
                  <th className="text-left p-2 font-bold">Capability</th>
                  <th className="p-2 font-bold text-amber-300">Bidoria</th>
                  <th className="p-2 font-bold">Others</th>
                </tr>
              </thead>
              <tbody>
                {cells.map(([cap, ...vals], i) => (
                  <tr key={i} className="border-b border-white/5 last:border-b-0">
                    <td className="p-2 text-white/85 leading-tight">{cap}</td>
                    {vals.map((v, j) => (
                      <td key={j} className="p-2 text-center">
                        {v ? (
                          <CheckCircle
                            size={13}
                            weight="fill"
                            className={j === 0 ? "text-amber-400 inline-block" : "text-emerald-400 inline-block opacity-80"}
                          />
                        ) : (
                          <XIcon size={11} className="text-white/25 inline-block" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "square") {
    // 1080 x 1080 — IG / X post / vertical share
    return (
      <div
        className="bg-zinc-950 text-white"
        style={{ width: 1080, height: 1080, position: "relative", overflow: "hidden" }}
        data-testid="bidoria-banner-square"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 30% 25%, rgba(245,158,11,0.20), transparent 55%), radial-gradient(circle at 75% 80%, rgba(29,120,171,0.18), transparent 55%)",
          }}
        />
        <div className="relative h-full flex flex-col p-14">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1 text-[12px] uppercase tracking-widest text-amber-300 font-bold mb-5">
              <Sparkle size={13} weight="fill" />
              Why Bidoria changes everything
            </div>
            <h1 className="text-[58px] font-black tracking-tight leading-[1.0]">
              Live auctions never had{" "}
              <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                anything like this.
              </span>
            </h1>
          </div>

          {/* Comparison table — feature-for-feature */}
          <div className="rounded-2xl bg-black/70 border border-amber-400/30 overflow-hidden flex-1 backdrop-blur-md shadow-2xl">
            <div className="bg-amber-500/10 border-b border-amber-400/20 px-6 py-3 text-center">
              <span className="text-[12px] uppercase tracking-widest text-amber-300 font-bold">
                Bidoria vs. the rest of the live-auction internet
              </span>
            </div>
            <table className="w-full text-[15px]">
              <thead>
                <tr className="border-b border-white/10 text-white/55 text-[11px] uppercase tracking-wider">
                  <th className="text-left p-3 font-bold">Capability</th>
                  <th className="p-3 font-bold text-amber-300">Bidoria</th>
                  <th className="p-3 font-bold">Others</th>
                </tr>
              </thead>
              <tbody>
                {cells.map(([cap, ...vals], i) => (
                  <tr key={i} className="border-b border-white/5 last:border-b-0">
                    <td className="p-3 text-white/85 font-medium">{cap}</td>
                    {vals.map((v, j) => (
                      <td key={j} className="p-3 text-center">
                        {v ? (
                          <CheckCircle
                            size={20}
                            weight="fill"
                            className={j === 0 ? "text-amber-400 inline-block" : "text-emerald-400 inline-block opacity-80"}
                          />
                        ) : (
                          <XIcon size={17} className="text-white/25 inline-block" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Wordmark footer */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-3">
              <span className="text-[36px] font-black bg-gradient-to-br from-amber-200 via-amber-400 to-orange-500 bg-clip-text text-transparent">
                Bidoria
              </span>
              <span className="text-white/40 text-[13px] uppercase tracking-[0.3em] font-bold">by</span>
              <span className="text-[30px] font-black text-white">Bidorah</span>
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.3em] text-white/40 font-bold">
              bidorah.com/demo · 2026
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default = "table" — 1080 x 1280, just the table cleanly
  return (
    <div
      className="bg-zinc-950 text-white"
      style={{ width: 1080, height: 1280, position: "relative", overflow: "hidden" }}
      data-testid="bidoria-banner-table"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 25%, rgba(245,158,11,0.16), transparent 55%), radial-gradient(circle at 75% 80%, rgba(29,120,171,0.14), transparent 55%)",
        }}
      />
      <div className="relative h-full p-12 flex flex-col justify-center">
        <div className="rounded-3xl bg-zinc-950/95 border border-amber-400/30 overflow-hidden shadow-2xl">
          <div className="bg-amber-500/10 border-b border-amber-400/20 px-6 py-4 text-center">
            <span className="text-[14px] uppercase tracking-widest text-amber-300 font-bold">
              Bidoria vs. the rest of the live-auction internet
            </span>
          </div>
          <table className="w-full text-[16px]">
            <thead>
              <tr className="border-b border-white/10 text-white/55 text-[11px] uppercase tracking-wider">
                <th className="text-left p-4 font-bold">Capability</th>
                <th className="p-4 font-bold text-amber-300">Bidorah · Bidoria</th>
                <th className="p-4 font-bold">Others</th>
              </tr>
            </thead>
            <tbody>
              {cells.map(([cap, ...vals], i) => (
                <tr key={i} className="border-b border-white/5 last:border-b-0">
                  <td className="p-4 text-white/85">{cap}</td>
                  {vals.map((v, j) => (
                    <td key={j} className="p-4 text-center">
                      {v ? (
                        <CheckCircle
                          size={22}
                          weight="fill"
                          className={j === 0 ? "text-amber-400 inline-block" : "text-emerald-400 inline-block opacity-80"}
                        />
                      ) : (
                        <XIcon size={19} className="text-white/25 inline-block" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-3 border-t border-white/5 bg-black/40 text-center">
            <span className="text-[12px] text-white/45">
              Comparison reflects publicly known features as of 2026. Bidoria is technology owned by Bidorah.
            </span>
          </div>
        </div>
        {/* Wordmark */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="text-[34px] font-black bg-gradient-to-br from-amber-200 via-amber-400 to-orange-500 bg-clip-text text-transparent">
              Bidoria
            </span>
            <span className="text-white/40 text-[13px] uppercase tracking-[0.3em] font-bold">by</span>
            <span className="text-[28px] font-black text-white">Bidorah</span>
          </div>
        </div>
      </div>
    </div>
  );
}
